import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import SupportBanner from "@/components/SupportBanner";

export default function Terms() {
  const today = new Date().toLocaleDateString("it-IT");
  return (
    <div className="min-h-screen bg-zen-bg py-10 px-6" data-testid="terms-page">
      <Seo
        title="Termini di Servizio — FreelanZen"
        description="Termini e condizioni per l'uso di FreelanZen, la piattaforma all-in-one per freelance italiani."
        path="/terms"
      />
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-zen-ink2 hover:text-zen-primary text-sm mb-6">
          <ArrowLeft size={14}/> Torna alla home
        </Link>
        <div className="zen-card p-8 md:p-10 space-y-6">
          <div>
            <img src="/brand/freelanzen-wordmark.png" alt="FreelanZen" className="h-8 w-auto mb-4"/>
            <h1 className="font-heading text-3xl md:text-4xl">Termini di Servizio</h1>
            <p className="text-zen-ink2 mt-1 text-sm">Ultimo aggiornamento: {today}</p>
          </div>

          <section className="space-y-3 text-sm leading-relaxed text-zen-ink">
            <h2 className="font-heading text-xl">1. Accettazione</h2>
            <p>Registrandoti a FreelanZen accetti integralmente i presenti Termini di Servizio. Se non accetti, non utilizzare il servizio.</p>

            <h2 className="font-heading text-xl mt-6">2. Descrizione del servizio</h2>
            <p>FreelanZen è un&apos;applicazione software-as-a-service (SaaS) per freelance che offre: gestione clienti, progetti, attività, fatturazione, preventivi, inbox unificata email/WhatsApp, report AI, tracking del tempo, generazione FatturaPA per SdI.</p>

            <h2 className="font-heading text-xl mt-6">3. Prova gratuita e piani a pagamento</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li><b>Prova gratuita</b>: 30 giorni con accesso completo a tutte le funzionalità. Nessuna carta richiesta.</li>
              <li><b>Piano Mensile</b>: € 9,99/mese IVA inclusa, con rinnovo automatico.</li>
              <li><b>Piano Annuale</b>: € 99,00/anno IVA inclusa (equivalente a € 8,25/mese), con rinnovo automatico.</li>
              <li>Puoi cancellare in qualsiasi momento — il piano rimane attivo fino alla fine del periodo pagato.</li>
              <li>Metodi di pagamento accettati: Stripe (carte, Apple Pay, Google Pay) e PayPal.</li>
            </ul>

            <h2 className="font-heading text-xl mt-6">4. Diritto di recesso e rimborsi</h2>
            <p>In accordo con il D.Lgs. 21/2014 (Codice del Consumo), hai diritto di recesso entro <b>14 giorni</b> dalla prima sottoscrizione a pagamento. Il rimborso viene effettuato entro 14 giorni dalla richiesta, tramite lo stesso metodo di pagamento utilizzato. Successivamente, non sono previsti rimborsi per periodi già fatturati.</p>

            <h2 className="font-heading text-xl mt-6">5. Obblighi dell&apos;utente</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>Fornire dati veritieri, aggiornati e completi.</li>
              <li>Non utilizzare il servizio per attività illecite o per inviare comunicazioni non richieste (spam).</li>
              <li>Non tentare di violare la sicurezza del servizio o di altri utenti.</li>
              <li>Mantenere riservate le credenziali di accesso.</li>
              <li>Rispettare i termini di servizio dei provider terzi collegati (Gmail, Meta WhatsApp, ecc.).</li>
            </ul>

            <h2 className="font-heading text-xl mt-6">6. Proprietà intellettuale</h2>
            <p>Il codice, il design, i marchi e i contenuti di FreelanZen sono proprietà esclusiva del Titolare. I dati inseriti dall&apos;utente (clienti, fatture, ecc.) restano di sua proprietà. FreelanZen ha licenza di utilizzarli esclusivamente per l&apos;erogazione del servizio.</p>

            <h2 className="font-heading text-xl mt-6">7. Limitazione di responsabilità</h2>
            <p>FreelanZen è fornito &quot;così com&apos;è&quot;. Pur impegnandoci a garantire alta affidabilità (target 99.5% uptime mensile), non garantiamo l&apos;assenza totale di interruzioni o errori. La responsabilità massima è limitata all&apos;importo pagato negli ultimi 12 mesi.</p>
            <p>FreelanZen non è responsabile per: perdite indirette, mancato guadagno, costi di sostituzione, violazioni di sicurezza dovute a credenziali compromesse dall&apos;utente, malfunzionamenti di servizi terzi collegati.</p>

            <h2 className="font-heading text-xl mt-6">8. Fatturazione elettronica SdI</h2>
            <p>La funzionalità di generazione XML FatturaPA produce file conformi allo schema ufficiale versione 1.2.2. La trasmissione al Sistema di Interscambio (SdI) è a carico dell&apos;utente, tramite il proprio provider accreditato o PEC. FreelanZen non garantisce l&apos;accettazione della fattura da parte dello SdI — è responsabilità dell&apos;utente verificare la correttezza dei dati inseriti.</p>

            <h2 className="font-heading text-xl mt-6">9. Sospensione e cessazione</h2>
            <p>Ci riserviamo il diritto di sospendere o chiudere account che violino i presenti Termini. In caso di cessazione, ti forniremo un export completo dei tuoi dati entro 30 giorni. Puoi cancellare l&apos;account in qualsiasi momento; per la conservazione fiscale delle fatture emesse resta valido l&apos;obbligo di legge (10 anni).</p>

            <h2 className="font-heading text-xl mt-6">10. Modifiche ai termini</h2>
            <p>Ci riserviamo il diritto di modificare i presenti Termini. Le modifiche sostanziali verranno comunicate via email almeno 30 giorni prima dell&apos;entrata in vigore. Continuando a utilizzare il servizio dopo la data di entrata in vigore, accetti le modifiche.</p>

            <h2 className="font-heading text-xl mt-6">11. Legge applicabile e foro competente</h2>
            <p>I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia il foro competente è quello del consumatore ai sensi dell&apos;art. 33 D.Lgs. 206/2005.</p>

            <p className="text-xs text-zen-ink2 mt-8">Per domande contatta <a href="mailto:supporto@freelanzen.it" className="text-zen-primary hover:underline">supporto@freelanzen.it</a></p>
          </section>
        </div>
      </div>
      <SupportBanner variant="site" />
    </div>
  );
}
